import { createContext, useContext, useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth';
import { setTierPreview } from '../services/api';

const TierContext = createContext();

// Same key the demo "Preview as" control has always used. Reused deliberately: the board's
// contract is ONE tier state, and a second key would be a second source of truth to drift.
const PREVIEW_TIER_KEY = 'demoTier';

export const TierProvider = ({ children }) => {
  const { isDemo, sandboxMode } = useAuth();
  const [previewTier, setPreviewTier] = useState(() => localStorage.getItem(PREVIEW_TIER_KEY) || 'plus');

  // Who may preview a tier they do not hold: the demo account (as before) and, now, any session
  // on the sandbox SURFACE — the Test Admin Dashboard. Both are pretend sessions, which is the
  // whole point of quoting them another tier's prices.
  //
  // `tierType` stays `null` for everyone else. A real member's tier is resolved by the server and
  // returned as `userTier` on the product response; the client must never re-derive it, so an
  // accidental read has to be an obvious absence rather than a confident wrong answer.
  const canPreviewTier = isDemo || sandboxMode;
  const tierType = canPreviewTier ? previewTier : null;

  // api.js is a plain module and cannot read React state, so the resolved value is mirrored into
  // it — exactly as AuthContext already mirrors `sandboxMode` via `setSandboxSurface`. Only the
  // sandbox surface is mirrored: the demo account puts its own `tierType` on the query from
  // CatalogPage, and pushing it here too would append the param twice.
  useEffect(() => {
    setTierPreview(sandboxMode ? tierType : null);
  }, [sandboxMode, tierType]);

  const setTier = (tier) => {
    if (!canPreviewTier) return;
    setPreviewTier(tier);
    localStorage.setItem(PREVIEW_TIER_KEY, tier);
  };

  return (
    <TierContext.Provider value={{ tierType, setTier, isDemo, canPreviewTier }}>
      {children}
    </TierContext.Provider>
  );
};

export const useTier = () => useContext(TierContext);
